<script setup lang="ts">
import {ref, useSlots} from 'vue'
import type {Placement} from '@floating-ui/vue'
import {CypressUniqueNameType} from '@toolify/client/cypress/types/CypressUniqueNameType'
import TooltipPopperComponent from './TooltipPopperComponent.vue'
import {TooltipTriggerType, useTooltipTrigger} from '../composables/useTooltipTrigger'

interface Props {
  trigger?: TooltipTriggerType
  visible?: boolean
  isDisabled?: boolean
  richContent?: boolean
  placement?: Placement
  distance?: number
  popperClass?: string
  canShow?: () => boolean
  triggers?: unknown[]
  handleResize?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  trigger: 'hover',
  visible: undefined,
  isDisabled: false,
  richContent: false,
  placement: 'top',
  distance: 5,
  popperClass: undefined,
  canShow: undefined,
  triggers: undefined,
  handleResize: undefined,
})

const slots = useSlots()
const referenceRef = ref<HTMLElement | null>(null)

const {isShown, popperId} = useTooltipTrigger(referenceRef, {
  trigger: () => props.trigger,
  visible: () => props.visible,
  isDisabled: () => props.isDisabled || !slots.tooltip,
  canShow: () => props.canShow?.() ?? true,
})
</script>

<template>
  <div
    ref="referenceRef"
    class="v-popper v-popper--theme-tooltip"
    :class="{'v-popper--shown': isShown}"
    :aria-describedby="isShown ? popperId : undefined"
    :data-cy="CypressUniqueNameType.FloatingTooltipComponent"
  >
    <slot/>
    <TooltipPopperComponent
      v-if="isShown"
      :reference="referenceRef"
      :popperId="popperId"
      :placement="props.placement"
      :distance="props.distance"
      :popperClass="props.popperClass"
      :richContent="props.richContent"
    >
      <slot name="tooltip"/>
    </TooltipPopperComponent>
  </div>
</template>
